/**
 * Status data source for the public /status page (C23).
 *
 * In production this fetches a small JSON document published from Cloud
 * Monitoring (SLO burn + uptime check results) to a public, cacheable URL —
 * so the status page never needs the live API or a signed-in user. Here the
 * source is *stubbed*: it returns the SLO targets from CLAUDE.md §8 with a
 * healthy default state, so the page renders deterministically offline and in
 * tests. Tests can override the fetch via the optional `fetcher` argument.
 */
import { z } from "zod";

/** One service-level objective and its current rolling state. */
export const sloStatusSchema = z.object({
  /** Stable id, e.g. "search-p95-latency". */
  id: z.string(),
  /** Human label shown on the page. */
  label: z.string(),
  /** Target description, e.g. "< 800ms" or "> 98%". */
  target: z.string(),
  /** Rolling-window state of the objective. */
  state: z.enum(["healthy", "degraded", "breached"]),
});
export type SloStatus = z.infer<typeof sloStatusSchema>;

/** Overall status payload: a coarse indicator, uptime %, and per-SLO rows. */
export const statusSnapshotSchema = z.object({
  /** Coarse roll-up shown in the banner. */
  overall: z.enum(["operational", "degraded", "outage"]),
  /** Rolling 30-day API uptime as a percentage (0..100). */
  uptimePercent: z.number().min(0).max(100),
  /** ISO timestamp of when the snapshot was produced. */
  updatedAt: z.string(),
  slos: z.array(sloStatusSchema),
});
export type StatusSnapshot = z.infer<typeof statusSnapshotSchema>;

/** URL the production status snapshot is published to (overridable via env). */
export const STATUS_SNAPSHOT_URL =
  (import.meta as { env?: Record<string, string | undefined> }).env?.["VITE_STATUS_URL"] ??
  "/status.json";

/**
 * Healthy default snapshot. Mirrors the SLO targets in CLAUDE.md §8 so the
 * page renders the right rows even when the published snapshot is unreachable.
 */
const FALLBACK_SNAPSHOT: StatusSnapshot = {
  overall: "operational",
  uptimePercent: 99.95,
  updatedAt: "1970-01-01T00:00:00.000Z",
  slos: [
    {
      id: "search-p95-latency",
      label: "Search latency (p95, keyword)",
      target: "< 800ms",
      state: "healthy",
    },
    {
      id: "scrape-success-rate",
      label: "Scrape success rate (rolling 7d)",
      target: "> 98%",
      state: "healthy",
    },
    {
      id: "ingest-lag",
      label: "Ingest lag (publish → indexed, p95)",
      target: "< 30 min",
      state: "healthy",
    },
  ],
};

/**
 * Fetch the current status snapshot. Falls back to {@link FALLBACK_SNAPSHOT}
 * when the source is unreachable or returns an unparseable body, so the page
 * always renders something useful and never throws.
 */
export async function fetchStatusSnapshot(
  fetcher: typeof fetch = globalThis.fetch,
): Promise<StatusSnapshot> {
  try {
    const res = await fetcher(STATUS_SNAPSHOT_URL, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return FALLBACK_SNAPSHOT;
    const parsed = statusSnapshotSchema.safeParse(await res.json());
    return parsed.success ? parsed.data : FALLBACK_SNAPSHOT;
  } catch {
    return FALLBACK_SNAPSHOT;
  }
}
